// Import necessary Firebase types
import { db as Database, firebaseImport } from '../../config/main_config';
import { nameFromUID } from '../utils/nameFromUID.util';
import { waitForCompanyID } from '../utils/waitForCompanyID.util';
import { generateMeetingAgenda } from '../utils/generateMeetingAgenda.util';
import { extractAssIDNumber } from '../utils/extractAssID.util'

// Define interfaces for parameters and assurance items
interface RetrieveParams {
    docRef?: firebaseImport.firestore.DocumentReference;
    page?: string;
    key?: string;
}

interface AssuranceItem {
    item: {
        checkStatus?: string;
        assignedTo?: string;
        checkDetail?: {
            active: string;
            check: string;
        };
        checkFrequency: {
            nextCheck: firebaseImport.firestore.Timestamp;
            checkDays?: string;
        };
        checkHistory: {
            [key: string]: {
                check: boolean;
                [key: string]: any;
            };
        };
        assID: string;
    };
    taskID: string;
    name: string;
    nextCheck: Date;
}

export class MeetingAgenda {
    private db: typeof Database;

    constructor(db = Database) {
        this.db = db;
    }

    /**
     * Makes text within [bold][/bold] tags bold using HTML <b> tags.
     * @param inputText The input string containing [bold] tags.
     * @returns The string with [bold] tags replaced by <b> tags.
     */
    makeTextBold(inputText: string): string {
        const regex = /\[bold\]([\s\S]*?)\[\/bold\]/g;
        const replacedText = inputText.replace(regex, '<b>$1</b>');
        return replacedText;
    }

    /**
     * Recursively replaces target strings with replacement strings within an object.
     * Applies makeTextBold to all string values.
     * @param obj The object to process.
     * @param targetString The string to be replaced.
     * @param replacementString The string to replace with.
     * @returns A new object with the replacements applied.
     */
    replaceStringInObject(obj: any, targetString: string, replacementString: string): any {
        if (typeof obj !== 'object' || obj === null) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => {
                if (typeof item === 'string') {
                    return this.makeTextBold(item.replace(new RegExp(targetString, 'g'), replacementString));
                } else {
                    return this.replaceStringInObject(item, targetString, replacementString);
                }
            });
        }

        return Object.keys(obj).reduce((acc: any, key: string) => {
            let value = obj[key];

            if (typeof value === 'string') {
                value = this.makeTextBold(value.replace(new RegExp(targetString, 'g'), replacementString));
            } else if (typeof value === 'object' && value !== null) {
                value = this.replaceStringInObject(value, targetString, replacementString);
            }

            acc[key] = value;
            return acc;
        }, {});
    }

    /**
     * Converts camelCase strings to normal spaced and capitalized strings.
     * @param camelCaseStr The camelCase string.
     * @returns The converted normal string.
     */
    camelToNormal(camelCaseStr: string): string {
        const spacedStr = camelCaseStr.replace(/([a-z])([A-Z])/g, '$1 $2');
        const localizedCasing = spacedStr.charAt(0).toUpperCase() + spacedStr.slice(1);
        return localizedCasing;
    }

    /**
     * Retrieves ISMS Manual data from the database based on provided parameters.
     * @param params The parameters containing docRef, page, and key.
     * @returns The retrieved data or undefined.
     */
    async retrieveISMSManualDataFromDatabase(params: RetrieveParams = {}): Promise<any | undefined> {
        const { docRef, page, key } = params;

        if (!docRef && !page) {
            console.error('[ISMS Manual] (retrieveISMSManualDataFromDatabase) Either docRef or page is required');
            return;
        }

        try {
            if (docRef) {
                const doc = await docRef.get();
                if (doc.exists) {
                    if (key && doc.data()?.hasOwnProperty(key)) {
                        let data = doc.data()![key];
                        if (typeof data === 'string') {
                            data = data
                                .replaceAll('{{ Company Name }}', (document.getElementById('tenancyName')?.textContent || ''))
                                .replaceAll('Acme Inc', (document.getElementById('tenancyName')?.textContent || ''))
                                .replaceAll('\n', '<br/>');
                        }
                        console.info('[ISMS Manual] (retrieveISMSManualDataFromDatabase) Key Data', data);
                        return data;
                    } else {
                        if (key) {
                            console.log('No such document and key pair!');
                            return;
                        } else {
                            let data = this.replaceStringInObject(doc.data(), '\n', '<br/>');
                            data = this.replaceStringInObject(
                                data,
                                '{{ Company Name }}',
                                (document.getElementById('tenancyName')?.textContent || ''),
                            );
                            data = this.replaceStringInObject(
                                data,
                                'Acme Inc',
                                (document.getElementById('tenancyName')?.textContent || ''),
                            );
                            console.info('[ISMS Manual] (retrieveISMSManualDataFromDatabase) docRef Data', data);
                            return data;
                        }
                    }
                } else {
                    console.log('No such document!');
                }
            }

            if (page) {
                const ismsPageRef = this.db
                    .collection('compliance')
                    .doc('ismsManual')
                    .collection('pages')
                    .doc(page);
                const ismsPage = await ismsPageRef.get();

                if (ismsPage.exists) {
                    if (key && ismsPage.data()?.hasOwnProperty(key)) {
                        let data = ismsPage.data()![key];
                        if (typeof data === 'string') {
                            data = data
                                .replaceAll('{{ Company Name }}', (document.getElementById('tenancyName')?.textContent || ''))
                                .replaceAll('Acme Inc', (document.getElementById('tenancyName')?.textContent || ''))
                                .replaceAll('\n', '<br/>');
                        }
                        console.info('[ISMS Manual] (retrieveISMSManualDataFromDatabase) Key Data', data);
                        return data;
                    } else {
                        if (key) {
                            console.log('No such document and key pair!');
                            return;
                        } else {
                            let data = this.replaceStringInObject(ismsPage.data(), '\n', '<br/>');
                            data = this.replaceStringInObject(
                                data,
                                '{{ Company Name }}',
                                (document.getElementById('tenancyName')?.textContent || ''),
                            );
                            data = this.replaceStringInObject(
                                data,
                                'Acme Inc',
                                (document.getElementById('tenancyName')?.textContent || ''),
                            );
                            console.info('[ISMS Manual] (retrieveISMSManualDataFromDatabase) docRef Data', data);
                            return data;
                        }
                    }
                } else {
                    console.log('No such document!');
                }
            }
        } catch (error) {
            console.error('Error retrieving ISMS Manual data:', error);
        }
    }

    private async getPerformanceData(): Promise<any> {
        return await this.retrieveISMSManualDataFromDatabase({
            page: 'performance',
        });
    }

    /**
     * Creates a popout modal with the meeting agenda.
     * @param performanceData The performance data to be used (currently unused).
     */
    async createPopout(performanceData: any = null): Promise<void> {
        if (!performanceData) {
            performanceData = await this.getPerformanceData();
        }
        const newDate = new Date();
        const MM = newDate.getMonth() + 1;
        const YYYY = newDate.getFullYear();

        const popout = `
            <div id="meetingAgendaModal" aria-hidden="true" class="modal fade new-modal-style" tabindex="-1" aria-labelledby="selectControlsModal" style="padding-right: 15px; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(3px); position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: none;" aria-modal="true" role="dialog" data-managedid="meetingAgendaModal">
                <div class="modal-dialog modal-fullscreen flex justify-center">
                    <div class="modal-content bg-white dark:bg-secondary">
                        <div class="modal-header" style="display: flex; flex-direction: row; justify-content: space-between; position: sticky; top: 0; z-index: 1000;">
                            <h5 class="modal-title text-foreground dark:!text-foreground" id="exampleModalLabel">Meeting Agenda</h5>
                            <div class="inputs flex flex-row w-full justify-end align-end items-end gap-4">
                                <div class="stacked-input" style="max-width: 50%">
                                    <div class="input-name">
                                        <label for="reviewPeriodForExport">Review Period</label>
                                    </div>
                                    <select id="reviewPeriodForExport" class="form-select" aria-label="Review Period (months)" onchange="ismsManualService.changeAssuranceItem(event)" required>
                                        <option value="3">3 Months</option>
                                        <option value="6">6 Months</option>
                                        <option value="12">12 Months</option>
                                    </select>
                                </div>
                                <div class="stacked-input" style="max-width: 50%">
                                    <div class="input-name">
                                        <label for="exportAgenda">Export Agenda</label>
                                    </div>
                                    <button id="exportAgenda" type="button" class="btn d-flex align-items-start mx-2 exportCreate" onclick="ismsManualService.exportMeetingAgenda(this)">
                                        <i class="fa-duotone fa-up color-orange" style="font-size: 1.5rem"></i>
                                        Export
                                    </button>
                                </div>
                                <button type="button" class="btn-close" onclick="this.closest('.modal').remove()" aria-label="Close"></button>
                            </div>
                        </div>
                        <div class="modal-body" style="max-height: calc(100vh - 80px); overflow-y: auto;">
                            <ul id="exportContent">
                                <li style="visibility: hidden;">[Clause 3.3.2: Test]</li>
                                <li>Status of actions from the previous meeting [Clause 9.3.2: Management review inputs]</li>
                                <li>Discussion on any changes to external or internal issues that are relevant to the management system [Clause 4.1: Understanding the organization and its context; Clause 4.2: Understanding the needs and expectations of interested parties]</li>
                                <li>Feedback on management system performance [Clause 9.1: Monitoring, measurement, analysis, and evaluation]</li>
                                <li>Review of any nonconformities and corrective actions [Clause 10.2: Nonconformity and corrective action]</li>
                                <li>Review of monitoring and management system metrics [Clause 9.1: Monitoring, measurement, analysis, and evaluation]</li>
                                <li>Review of any audit results [Clause 9.2: Internal audit]</li>
                                <li>Review of management system objectives and their fulfilment [Clause 6.2: Objectives and planning to achieve them]</li>
                                <li>Review of any feedback from interested parties [Clause 4.2: Understanding the needs and expectations of interested parties]</li>
                                <li>Status of risks, including any updates to Risk Assessments [Clause 6.1.2: Information security risk assessment]</li>
                                <li style="list-style-type: none">Review Assurance Items
                                    <ul class="assuranceItem"></ul>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>`;

        document.body.insertAdjacentHTML('beforeend', popout);
        this.changeAssuranceItem();
    }

    /**
     * Exports the meeting agenda by generating a Word document.
     * @param element The button element that was clicked.
     */
    async exportMeetingAgenda(element: HTMLElement): Promise<void> {
        const reviewPeriodElement = document.querySelector<HTMLSelectElement>('#reviewPeriodForExport');
        const reviewPeriod = reviewPeriodElement ? parseInt(reviewPeriodElement.value, 10) : 3;

        element.innerHTML = '<i class="fa-duotone fa-spinner-third fa-spin"></i> Exporting...';

        await generateMeetingAgenda((window as any).companyID, reviewPeriod);

        element.innerHTML = '<i class="fa-duotone fa-check"></i> Exported';

        setTimeout(() => {
            element.innerHTML = '<i class="fa-duotone fa-up color-orange"></i> Export';
        }, 750);
    }

    /**
     * Changes the assurance items based on the selected review period.
     * @param e The event triggering the change.
     */
    async changeAssuranceItem(e?: Event | null): Promise<void> {
        const companyID = await waitForCompanyID();
        const target = e !== null ? e?.target as HTMLSelectElement : null;
        const months = target && target.value ? parseInt(target.value, 10) : 3;
        const assurance: AssuranceItem[] = [];
        const assuranceItem = document.querySelector<HTMLUListElement>('.assuranceItem');

        if (!assuranceItem) {
            console.error('Assurance item container not found.');
            return;
        }

        assuranceItem.innerHTML = '';

        const assuranceDB = this.db
            .collection('customers')
            .doc(companyID)
            .collection('compliance')
            .doc('assurance');

        try {
            const doc = await assuranceDB.get();
            if (!doc.exists) {
                console.error('Assurance document does not exist.');
                return;
            }

            const data = doc.data();
            if (!data) {
                console.error('No data found in assurance document.');
                return;
            }

            const currentDate = new Date();
            const pastDate = new Date();
            pastDate.setMonth(pastDate.getMonth() - months);

            for (const itemKey in data) {
                const item = data[itemKey];
                if (item?.checkDetail?.active === 'true') {
                    const nextCheck = item.checkFrequency.nextCheck.toDate();
                    const checkDaysFB = parseInt(item.checkFrequency.checkDays || '0', 10);
                    const lastCheck = new Date(nextCheck);
                    lastCheck.setDate(lastCheck.getDate() - checkDaysFB);

                    if (
                        (nextCheck >= pastDate && nextCheck <= currentDate) ||
                        (lastCheck >= pastDate && lastCheck <= currentDate)
                    ) {
                        for (const historyKey in item.checkHistory) {
                            const historyItem = item.checkHistory[historyKey];
                            if (historyItem.check) {
                                assurance.push({
                                    item: historyItem,
                                    taskID: item.assID,
                                    name: item.checkDetail.check,
                                    nextCheck: nextCheck,
                                });
                            }
                        }
                    }
                }
            }

            // Deduplicate based on 'taskID'
            const uniqueAssuranceMap = new Map<string, AssuranceItem>();
            assurance.forEach((ass) => {
                if (!uniqueAssuranceMap.has(ass.taskID)) {
                    uniqueAssuranceMap.set(ass.taskID, ass);
                }
            });
            const uniqueAssurance = Array.from(uniqueAssuranceMap.values());

            // Sorting Logic:
            uniqueAssurance.sort((a, b) => {
                const statusA = a.item.checkStatus?.toLowerCase() || '';
                const statusB = b.item.checkStatus?.toLowerCase() || '';

                if (statusA === statusB) {
                    const numA = extractAssIDNumber(a.taskID);
                    const numB = extractAssIDNumber(b.taskID);
                    return numA - numB;
                }
                return statusA === 'incomplete' ? -1 : 1;
            });

            for (const ass of uniqueAssurance) {
                if (!ass.item.assignedTo) continue;

                try {
                    const name = await nameFromUID(ass.item.assignedTo);

                    const tenancyName = (window.companyName || '').replace(/\bAcme,?\s+Inc\.?\b/gi, '');

                    const li = `
                        <li>
                            <span>${ass.name.replace(/\bAcme,?\s+Inc\.?\b/gi, tenancyName)}&nbsp;-&nbsp;</span>
                            <span>${ass.item.checkStatus ? ass.item.checkStatus.charAt(0).toUpperCase() + ass.item.checkStatus.slice(1) : 'Unknown'}&nbsp;-&nbsp;</span>
                            <span>${name}</span>
                        </li>
                    `;
                    assuranceItem.insertAdjacentHTML('beforeend', li);
                } catch (error) {
                    console.error(`Error fetching user data for ID ${ass.item.assignedTo}:`, error);
                }
            }
        } catch (error) {
            console.error('Error fetching assurance data:', error);
        }
    }

    /**
     * Attaches the exportMeetingAgenda and changeAssuranceItem methods to the window object.
     * This allows them to be called from inline event handlers in the HTML.
     */
    attachToWindow(): void {
        (window as any).ismsManualService = this;
    }
}

/*
    // Usage Example:
    // Create an instance of the service
    const meetingAgenda = new MeetingAgenda();

    // Attach methods to the window object for inline event handlers
    meetingAgenda.attachToWindow();

    // Now, you can call meetingAgenda.createPopout(performanceData) to create the modal
*/